import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';

import { call } from '../utils';

import styles from './TextInline.less';

const cxb = cx.bind(styles);

/**
 * **Access:**
 * ```js
 * import { TextInline } from '@iss/client-react-components'
 * ```
 *
 * A styled editable text Input. The text will not appear as a field until focused.
 * This will inherit the font styles from its parent.
 *
 */
export default class TextInline extends React.PureComponent {
  static propTypes = {
    /** @ignore */
    className: PropTypes.string,

    /** Mark the control disabled. */
    disabled: PropTypes.bool,

    /** Mark the control as errored. */
    error: PropTypes.bool,

    /** Specifies the Hint Text when the input is empty. */
    placeholder: PropTypes.string,

    /**
     * A callback to recieve the changed value.
     * @param {string} value The new value.
     */
    onChange: PropTypes.func,

    /**
     * A callback to recieve the changed value when the editor is commited. (enter key)
     * @param {string} value The new value.
     */
    onCommit: PropTypes.func,

    /**
     * A callback to be called whent he editor is reset
     * @param {string} value The new value.
     */
    onReset: PropTypes.func,

    /** The value to put into the input control */
    value: PropTypes.string,

    /** For uncontrolled operation, this sets the initial value */
    initialValue: PropTypes.string,
  };

  static getDerivedStateFromProps(props, { value, ...state }) {
    // statfull to stateless transition:
    if (props.value != null && value != null) {
      return state;
    }

    if (props.initialValue != null && value == null) {
      return {
        ...state,
        value: props.initialValue,
      };
    }

    return null;
  }

  componentWillUnmount() {
    this.setState = () => {};
  }

  /** @private */
  state = {};

  inputRef = React.createRef();

  /**
   * DOM Reference to the input
   *
   * @private
   */
  get input() {
    return this.inputRef.current;
  }

  /**
   * Getter property (not a method) for the validity of the input
   *
   * @see Docs: {@link https://developer.mozilla.org/en-US/docs/Web/API/ValidityState ValidityState}
   * @public
   */
  get validity() {
    return (this.input || {}).validity;
  }

  get value() {
    return this.props.value || this.state.value || '';
  }

  /**
   * focus the input
   *
   * @public
   * @method focus
   * @return {void}
   */
  focus() {
    this.onFocus();
  }

  /**
   * Called when the input changes.
   *
   * @private
   * @method onChange
   * @param  {Event} e The normalized browser event instance
   * @return {void}
   */
  onChange = ({ target: { value } }) => {
    call(this.props.onChange, value);

    /* istanbul ignore else */
    if (this.props.value == null) {
      this.setState({ value });
    }
  };

  onBlur = () => {
    const { onReset, initialValue = '' } = this.props;
    call(onReset, this.value);
    this.setState({ active: false });

    /* istanbul ignore else */
    if (this.props.value == null) {
      this.setState({ value: initialValue });
    }
  };

  onFocus = () => {
    /* istanbul ignore else */
    if (!this.state.active) {
      this.setState({ active: true }, () => this.putCursorAtTheEnd());
    }
  };

  onKeyDown = (e) => {
    const {
      value,
      props: { onCommit },
    } = this;

    if (e.key === 'Enter') {
      call(onCommit, value);
    } else if (e.key === 'Escape') {
      this.onBlur();
    }
  };

  /**
   * Clears the input. (by calling `onChange('')`)
   *
   * @public
   * @method clear
   * @return {void}
   */
  clear = () => {
    const { props } = this;

    call(props.onChange, '');
    call(props.onReset, '');

    /* istanbul ignore else */
    if (props.value == null) {
      this.setState({ value: '' });
    }

    this.focus();
  };

  putCursorAtTheEnd() {
    const {
      input,
      value: { length: x },
    } = this;
    if (!input) {
      return;
    }

    /* istanbul ignore else */
    if (input.setSelectionRange) {
      input.focus();
      input.setSelectionRange(x, x);
    }
  }

  render() {
    const {
      small,
      state: { active },
      props: { className, error, placeholder, initialValue, style, ...props },
      value,
    } = this;

    const text = value || (!active && placeholder) || '\xa0';
    const { disabled } = props;

    delete props.onReset;
    delete props.onCommit;

    return (
      <span
        disabled={disabled}
        style={style}
        tabIndex={disabled || active ? '-1' : '0'}
        onFocus={disabled ? null : this.onFocus}
        className={cxb('root', { error, small, disabled, active, placeholder: !value }, className)}
      >
        <span className={cxb('value')} onClick={disabled ? null : this.onFocus}>
          {text}
        </span>
        <input
          {...props}
          ref={this.inputRef}
          placeholder={placeholder}
          onBlur={disabled ? null : this.onBlur}
          onChange={disabled ? null : this.onChange}
          onKeyDown={disabled ? null : this.onKeyDown}
          value={value || ''}
          tabIndex={active ? '0' : '-1'}
        />
        <i className="icon-pencil" />
      </span>
    );
  }
}
